/**
 * ActionPlanAccordion — four collapsible priority sections, all open by default.
 * Uses data-state and data-accordion-content attributes so the parent
 * ResultsPage's @media print CSS can force-expand all sections.
 *
 * D-12: UNSURE action items render the "Needs Verification" badge.
 * Cost is shown only for DIY items (parts estimate); contractor items omit cost
 * since labour varies too much by region.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/utils/cn';
import { ROOM_LABELS } from '../utils/roomMap';
import type { ActionItem, Priority } from '../types/homeAudit.types';

type ActionPlanAccordionProps = {
  actionItems: ActionItem[];
};

const TIER_ORDER: Priority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const TIER_LABELS: Record<Priority, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};
const TIER_BADGE: Record<Priority, string> = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-700 border-blue-200',
};

type SectionProps = {
  priority: Priority;
  items: ActionItem[];
};

const DEFAULT_OPEN: Record<Priority, boolean> = {
  CRITICAL: true,
  HIGH: true,
  MEDIUM: false,
  LOW: false,
};

const AccordionSection = ({ priority, items }: SectionProps) => {
  const [open, setOpen] = useState(DEFAULT_OPEN[priority]);
  return (
    <Card data-state={open ? 'open' : 'closed'} data-priority={priority}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid={`accordion-trigger-${priority}`}
      >
        <span className="flex items-center gap-3">
          <Badge className={cn('border', TIER_BADGE[priority])}>
            {TIER_LABELS[priority]}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {items.length} item{items.length === 1 ? '' : 's'}
          </span>
        </span>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <div
        data-accordion-content
        className={cn('px-4 pb-4 space-y-3', !open && 'hidden')}
      >
        {items.map((item) => {
          const isUnsure = item.answer === 'UNSURE';
          const showCost = item.isDiy && item.costLow > 0;
          return (
            <CardContent
              key={item.id}
              className={cn(
                'p-4 rounded-md border',
                isUnsure ? 'bg-blue-50 border-blue-200' : 'bg-background border-border',
              )}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-base font-medium">{item.title}</p>
                    {isUnsure && (
                      <Badge className="bg-blue-100 text-blue-700 border border-blue-200">
                        Needs Verification
                      </Badge>
                    )}
                    {item.isDiy && (
                      <Badge className="bg-green-100 text-green-700 border border-green-200">
                        DIY
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                  <p className="text-xs text-muted-foreground">{ROOM_LABELS[item.roomArea]}</p>
                  <p className="text-xs font-medium text-muted-foreground">
                    {isUnsure ? 'Verify this' : 'Fix this'}
                  </p>
                </div>
                {showCost && (
                  <p className="text-sm font-medium whitespace-nowrap">
                    ${item.costLow} - ${item.costHigh}
                  </p>
                )}
              </div>
            </CardContent>
          );
        })}
      </div>
    </Card>
  );
};

export const ActionPlanAccordion = ({ actionItems }: ActionPlanAccordionProps) => {
  return (
    <div className="space-y-3" data-testid="action-plan-accordion">
      {TIER_ORDER.map((tier) => (
        <AccordionSection
          key={tier}
          priority={tier}
          items={actionItems.filter((i) => i.priority === tier)}
        />
      ))}
    </div>
  );
};
